// Installs the read cache (read-cache.ts) as the page's fetch BEFORE any API client exists: openapi-fetch
// takes `globalThis.fetch` when a client is created, and the SDK hosts create theirs when their module
// loads. main.tsx imports this first (after the CSP nonce).
import { config } from '../config.ts';
import { watchFetch } from './connection.ts';
import { createReadCache } from './read-cache.ts';

const safeStorage = (() => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();

/** The browser's own fetch, before the cache (and browser tracing, when on) wrap it. */
export const browserFetch: typeof fetch = globalThis.fetch.bind(globalThis);
let network: typeof fetch = browserFetch;
/**
 * What the cache sends a request to the network with. Browser tracing (tracing/browser-tracing.ts) puts its
 * traced fetch here: the API clients hold the cache's fetch from when they were made, so patching
 * globalThis.fetch later would never see their calls.
 */
export function setNetworkFetch(f: typeof fetch) {
  network = f;
}

export const readCache = createReadCache({
  apiBase: new URL(config.VITE_API_BASE_URL, window.location.origin).toString(),
  // An api that does not answer is reported ("Reconnecting…", connection.ts), whichever client asked.
  fetch: watchFetch((input, init) => network(input, init)),
  storage: safeStorage,
});

globalThis.fetch = readCache.fetch;

// The navigation window is for the reads a page makes as it opens; a click or a key on it (Refresh,
// Save, a filter) is the person asking for now. A click on a nav item opens a new window right after.
for (const type of ['pointerdown', 'keydown'] as const)
  window.addEventListener(type, () => readCache.interacted(), { capture: true, passive: true });
